import { EmptyState, ErrorState, LoadingState, RADIUS } from "@elyxeer/ui";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import SearchIcon from "@mui/icons-material/Search";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useDeferredValue, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError, deleteCover, fetchCoverLibrary } from "~/lib/api";
import { queryKeys } from "~/lib/query-client";
import type { CoverImageAdmin } from "~/types/covers-admin";
import { CONTENT_TOPICS, type ContentTopic } from "~/types/news-admin";

const PAGE_SIZE = 48;

/**
 * The cover library as a picker: every photograph already published — the release
 * library and everything editors have uploaded or generated — offered as a reusable
 * header, so a new article can wear an existing photo instead of paying for another.
 *
 * Picking only hands the URL back; the form saves it like any other field. Unused
 * covers can be removed here, and only unused ones: the API refuses a cover that an
 * article, event or highlight still shows, and the button is not offered for those.
 */
export function CoverLibraryDialog({
  open,
  currentUrl,
  onClose,
  onPick,
}: {
  open: boolean;
  /** The cover the form currently holds, marked as selected in the grid. */
  currentUrl: string | null;
  onClose: () => void;
  onPick: (url: string) => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [topic, setTopic] = useState<ContentTopic | undefined>(undefined);
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search.trim());
  const [pendingDelete, setPendingDelete] = useState<CoverImageAdmin | null>(
    null,
  );
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const library = useInfiniteQuery({
    queryKey: queryKeys.covers({ topic, search: deferredSearch }),
    queryFn: ({ pageParam, signal }) =>
      fetchCoverLibrary(
        { topic, search: deferredSearch, limit: PAGE_SIZE, offset: pageParam },
        signal,
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((sum, page) => sum + page.items.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    enabled: open,
    // A new filter keeps the old grid on screen until its first page arrives.
    placeholderData: keepPreviousData,
  });

  const remove = useMutation({
    mutationFn: (cover: CoverImageAdmin) => deleteCover(cover.id),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.coversRoot }),
    onError: (error) => {
      const inUse = error instanceof ApiError && error.status === 409;
      setErrorKey(inUse ? "covers.errors.inUse" : "common.somethingWentWrong");
      // The grid's usage count was stale; refetch so the tile loses its delete button.
      if (inUse)
        void queryClient.invalidateQueries({ queryKey: queryKeys.coversRoot });
    },
    onSettled: () => setPendingDelete(null),
  });

  const items = library.data?.pages.flatMap((page) => page.items) ?? [];
  const total = library.data?.pages[0]?.total ?? 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      data-testid="covers.dialog"
    >
      <DialogTitle>{t("covers.title")}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="T2" sx={{ color: "text.secondary" }}>
            {t("covers.intro")}
          </Typography>

          <TextField
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t("covers.search")}
            size="small"
            slotProps={{
              htmlInput: { "data-testid": "covers.search", maxLength: 120 },
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ maxWidth: 420 }}
          />

          <Box
            role="group"
            aria-label={t("covers.topicFilter")}
            sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}
          >
            <Chip
              label={t("covers.allTopics")}
              color={topic === undefined ? "primary" : "default"}
              variant={topic === undefined ? "filled" : "outlined"}
              onClick={() => setTopic(undefined)}
              data-testid="covers.topic-all"
            />
            {CONTENT_TOPICS.map((option) => (
              <Chip
                key={option}
                label={t(`topics.${option}`)}
                color={topic === option ? "primary" : "default"}
                variant={topic === option ? "filled" : "outlined"}
                onClick={() => setTopic(option)}
                data-testid={`covers.topic-${option}`}
              />
            ))}
          </Box>

          {errorKey ? (
            <Alert severity="error" onClose={() => setErrorKey(null)}>
              {t(errorKey)}
            </Alert>
          ) : null}

          {library.isPending ? <LoadingState /> : null}
          {library.isError ? (
            <ErrorState onRetry={() => void library.refetch()} />
          ) : null}
          {library.isSuccess && items.length === 0 ? (
            <EmptyState message={t("covers.empty")} />
          ) : null}

          {items.length > 0 ? (
            <Box
              data-testid="covers.grid"
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                gap: 2,
              }}
            >
              {items.map((cover) => (
                <CoverTile
                  key={cover.id}
                  cover={cover}
                  selected={cover.url === currentUrl}
                  onPick={() => {
                    onPick(cover.url);
                    onClose();
                  }}
                  onDelete={() => {
                    setErrorKey(null);
                    setPendingDelete(cover);
                  }}
                />
              ))}
            </Box>
          ) : null}

          {library.hasNextPage ? (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Button
                onClick={() => void library.fetchNextPage()}
                disabled={library.isFetchingNextPage}
                data-testid="covers.more"
              >
                {t("covers.more", { shown: items.length, total })}
              </Button>
            </Box>
          ) : null}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("common.cancel")}</Button>
      </DialogActions>

      <Dialog
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
      >
        <DialogTitle>{t("covers.deleteTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t("covers.deleteBody")}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPendingDelete(null)}
            disabled={remove.isPending}
          >
            {t("common.cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={remove.isPending}
            onClick={() => pendingDelete && remove.mutate(pendingDelete)}
            data-testid="covers.delete-confirm"
          >
            {t("covers.deleteConfirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

function CoverTile({
  cover,
  selected,
  onPick,
  onDelete,
}: {
  cover: CoverImageAdmin;
  selected: boolean;
  onPick: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        position: "relative",
        border: "2px solid",
        borderColor: selected ? "primary.main" : "divider",
        borderRadius: `${RADIUS.md}px`,
        overflow: "hidden",
        backgroundColor: "background.paper",
      }}
    >
      <ButtonBase
        onClick={onPick}
        aria-label={t("covers.use", { label: cover.label ?? "" })}
        data-testid={`covers.tile-${cover.id}`}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          width: "100%",
          textAlign: "left",
        }}
      >
        <Box
          component="img"
          src={cover.url}
          alt=""
          loading="lazy"
          sx={{
            width: "100%",
            aspectRatio: "16 / 9",
            objectFit: "cover",
            display: "block",
            backgroundColor: "surface.inset",
          }}
        />
        <Box
          sx={{
            padding: 1.25,
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
          }}
        >
          <Typography
            variant="T3"
            sx={{
              color: "text.primary",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              minHeight: "2.6em",
            }}
          >
            {cover.label ?? cover.origin_slug ?? "—"}
          </Typography>
          <Typography variant="T3" sx={{ color: "text.muted" }}>
            {t(`covers.source.${cover.source}`)}
            {" · "}
            {cover.usage_count > 0
              ? t("covers.usedBy", { count: cover.usage_count })
              : t("covers.unused")}
          </Typography>
        </Box>
      </ButtonBase>

      {selected ? (
        <CheckCircleIcon
          color="primary"
          sx={{
            position: "absolute",
            top: 8,
            left: 8,
            backgroundColor: "background.paper",
            borderRadius: "50%",
          }}
        />
      ) : null}

      {/* Not for the cover this form holds: it is about to be used, whatever the count says. */}
      {cover.usage_count === 0 && !selected ? (
        <Tooltip title={t("covers.delete")}>
          <IconButton
            size="small"
            onClick={onDelete}
            aria-label={t("covers.delete")}
            data-testid={`covers.delete-${cover.id}`}
            sx={{
              position: "absolute",
              top: 6,
              right: 6,
              backgroundColor: "background.paper",
              "&:hover": { backgroundColor: "background.paper" },
            }}
          >
            <DeleteOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}
    </Box>
  );
}
